import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import Header from "@/components/Header";
import GoldenParticles from "@/components/GoldenParticles";
import { api, resolveImg, track } from "@/lib/api";
import { Download, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function AccessPage() {
  const { lang, t } = useLang();
  const [mobile, setMobile] = useState("");
  const [dob, setDob] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!/^[6-9]\d{9}$/.test(mobile) || !dob) { toast.error(t("access.err")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/access", { mobile, dob });
      setItems(data.items);
      track("access_again_view", { count: data.items.length });
    } catch (err) {
      setItems(null);
      toast.error(err?.response?.data?.detail || t("access.notfound"));
    } finally { setBusy(false); }
  };

  const download = async (it) => {
    try {
      const r = await fetch(resolveImg(it.image_url));
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `SHREE-RAGO-${it.deity.replace(/\s+/g, "-")}.jpg`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { window.open(resolveImg(it.image_url), "_blank"); }
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory relative overflow-hidden" data-testid="access-page">
      <Header onCtaClick={() => (window.location.href = `/${lang}#form`)} />
      <GoldenParticles density={30} />
      <main className="relative max-w-xl mx-auto px-6 pt-32 pb-24">
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full border border-gold-active flex items-center justify-center text-gold-hi mb-5"><KeyRound size={20} /></div>
          <p className="text-xs uppercase tracking-[0.35em] text-gold mb-3">{t("access.eyebrow")}</p>
          <h1 className="font-serif-lux text-3xl sm:text-5xl gold-shimmer">{t("access.title")}</h1>
          <p className="text-muted-warm text-sm mt-5 leading-relaxed">{t("access.body")}</p>
        </div>

        {items === null || items.length === 0 ? (
          <form onSubmit={submit} className="glass-panel rounded-3xl p-6 sm:p-8 mt-10 space-y-5" data-testid="access-form">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-gold">{t("form.mobile")}</span>
              <div className="flex mt-2">
                <span className="input-warm input-prefix">+91</span>
                <input data-testid="access-mobile" type="tel" inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder={t("form.mobile.ph")} className="input-warm input-suffix" required />
              </div>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-gold">{t("form.dob")}</span>
              <input data-testid="access-dob" type="date" value={dob} onChange={(e) => setDob(e.target.value)} className="input-warm mt-2" required />
            </label>
            <button disabled={busy} className="btn-gold w-full text-sm disabled:opacity-60" data-testid="access-submit">{busy ? "…" : t("access.cta")}</button>
            <p className="text-[11px] text-muted-warm text-center">{t("access.hint")}</p>
          </form>
        ) : (
          <div className="mt-10 space-y-6" data-testid="access-results">
            <p className="text-center text-sm text-gold-hi">{t("access.found").replace("{n}", String(items.length))}</p>
            {items.map((it) => (
              <div key={it.lead_id} className="glass-panel rounded-3xl overflow-hidden grid grid-cols-1 sm:grid-cols-5" data-testid={`access-item-${it.lead_id}`}>
                <div className="sm:col-span-2 aspect-[9/16] sm:aspect-auto">
                  <img src={resolveImg(it.image_url)} alt={it.deity} className="w-full h-full object-cover" />
                </div>
                <div className="sm:col-span-3 p-6 flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-gold">{t("result.selected_for")}</div>
                    <div className="font-serif-lux text-2xl text-ivory mt-1">{it.full_name}</div>
                    <div className="text-gold-hi text-sm mt-1">{it.deity}</div>
                    {it.paid_at && <div className="text-[11px] text-muted-warm mt-2">{new Date(it.paid_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</div>}
                  </div>
                  <div className="mt-6 flex flex-col gap-2">
                    <button onClick={() => download(it)} className="btn-gold text-xs inline-flex items-center justify-center gap-2" data-testid={`access-download-${it.lead_id}`}><Download size={14} /> {t("result.download")}</button>
                    <Link to={`/${lang}/reveal/${it.lead_id}`} className="btn-outline-gold text-xs text-center" data-testid={`access-open-${it.lead_id}`}>{t("access.open")}</Link>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={() => setItems(null)} className="block mx-auto text-[11px] uppercase tracking-widest text-muted-warm hover:text-gold-hi" data-testid="access-search-again">{t("access.again")}</button>
          </div>
        )}

        <div className="text-center mt-12">
          <Link to={`/${lang}`} className="text-xs uppercase tracking-[0.25em] text-muted-warm hover:text-gold-hi">{t("result.back")}</Link>
        </div>
      </main>
    </div>
  );
}
